import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PromptTable = ({ prompts, selectedPrompts, onSelectPrompt, onSelectAll, onEdit, onDuplicate, onCopy, onShare, onDelete }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getQualityColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getQualityBadge = (score) => {
    if (score >= 90) return 'bg-success/10 text-success';
    if (score >= 70) return 'bg-warning/10 text-warning';
    return 'bg-error/10 text-error';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto custom-scrollbar">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={selectedPrompts?.length === prompts?.length && prompts?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  indeterminate={selectedPrompts?.length > 0 && selectedPrompts?.length < prompts?.length}
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">AI Tool</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Use Case</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Prompt</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Quality</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {prompts?.map((prompt) => (
              <React.Fragment key={prompt?.id}>
                <tr className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedPrompts?.includes(prompt?.id)}
                      onChange={() => onSelectPrompt(prompt?.id)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {prompt?.date}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Icon name={prompt?.toolIcon} size={16} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">{prompt?.tool}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-secondary/10 text-secondary text-xs font-medium">
                      {prompt?.useCase}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-foreground line-clamp-2">{prompt?.snippet}</p>
                      <button
                        onClick={() => toggleRow(prompt?.id)}
                        className="flex-shrink-0 text-primary hover:text-primary/80 transition-colors"
                      >
                        <Icon name={expandedRow === prompt?.id ? "ChevronUp" : "ChevronDown"} size={16} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${getQualityColor(prompt?.qualityScore)}`}>
                        {prompt?.qualityScore}
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${getQualityBadge(prompt?.qualityScore)}`}>
                        {prompt?.qualityScore >= 90 ? 'Excellent' : prompt?.qualityScore >= 70 ? 'Good' : 'Fair'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => prompt?.isFavorite && onEdit(prompt)}
                        className="p-1.5 hover:bg-muted rounded-md transition-colors"
                        title="Edit"
                      >
                        <Icon name="Edit2" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => onDuplicate(prompt)}
                        className="p-1.5 hover:bg-muted rounded-md transition-colors"
                        title="Duplicate"
                      >
                        <Icon name="Copy" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => onCopy(prompt)}
                        className="p-1.5 hover:bg-muted rounded-md transition-colors"
                        title="Copy to clipboard"
                      >
                        <Icon name="Clipboard" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => onShare(prompt)}
                        className="p-1.5 hover:bg-muted rounded-md transition-colors"
                        title="Share"
                      >
                        <Icon name="Share2" size={16} className="text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === prompt?.id && (
                  <tr className="border-b border-border bg-muted/30">
                    <td colSpan="7" className="px-4 py-4">
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-2">Full Prompt:</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">{prompt?.fullText}</p>
                        </div>
                        <div className="flex items-center gap-4 pt-2">
                          <Button variant="outline" size="sm" iconName="Edit2" iconPosition="left" onClick={() => onEdit(prompt)}>
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" iconName="Copy" iconPosition="left" onClick={() => onDuplicate(prompt)}>
                            Duplicate
                          </Button>
                          <Button variant="outline" size="sm" iconName="Clipboard" iconPosition="left" onClick={() => onCopy(prompt)}>
                            Copy
                          </Button>
                          <Button variant="outline" size="sm" iconName="Share2" iconPosition="left" onClick={() => onShare(prompt)}>
                            Share
                          </Button>
                          <Button variant="destructive" size="sm" iconName="Trash2" iconPosition="left" onClick={() => onDelete(prompt)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {prompts?.map((prompt) => (
          <div key={prompt?.id} className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Checkbox
                checked={selectedPrompts?.includes(prompt?.id)}
                onChange={() => onSelectPrompt(prompt?.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={prompt?.toolIcon} size={16} className="text-primary flex-shrink-0" />
                  <span className="text-sm font-semibold text-foreground">{prompt?.tool}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-secondary/10 text-secondary text-xs font-medium">
                    {prompt?.useCase}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{prompt?.snippet}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{prompt?.date}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${getQualityColor(prompt?.qualityScore)}`}>
                      {prompt?.qualityScore}
                    </span>
                    <button
                      onClick={() => toggleRow(prompt?.id)}
                      className="text-primary hover:text-primary/80 transition-colors"
                    >
                      <Icon name={expandedRow === prompt?.id ? "ChevronUp" : "ChevronDown"} size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {expandedRow === prompt?.id && (
              <div className="mt-3 pt-3 border-t border-border space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">Full Prompt:</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{prompt?.fullText}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" iconName="Edit2" iconPosition="left" onClick={() => onEdit(prompt)} fullWidth>
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" iconName="Copy" iconPosition="left" onClick={() => onDuplicate(prompt)} fullWidth>
                    Duplicate
                  </Button>
                  <Button variant="outline" size="sm" iconName="Clipboard" iconPosition="left" onClick={() => onCopy(prompt)} fullWidth>
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" iconName="Share2" iconPosition="left" onClick={() => onShare(prompt)} fullWidth>
                    Share
                  </Button>
                </div>
                <Button variant="destructive" size="sm" iconName="Trash2" iconPosition="left" onClick={() => onDelete(prompt)} fullWidth>
                  Delete
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromptTable;